import {isRollCarpetFinish,isCarpetTileFinish,buildPlan,mergeAreas} from '@artemis-group/aiw-contracts';
import {roomSvgPath} from '@artemis-group/aiw-contracts';
/**
 * The canvas drawing → the site assessment.
 *
 * Jacky, 17 September, mid-draft: *"how come my canvas drawing is not imported or the room dimensions"*.
 * There was no import path at all — the assessment was manual entry with a rectangle helper. The
 * drawing exists (the studio records every room's boundary in metres, its floor, its laser survey and
 * the seam alignment), and `GET /api/jobs/:ref/canvas-rooms` maps it into assessment rooms.
 *
 * TWO THINGS THIS DELIBERATELY SAYS OUT LOUD:
 *   · a proposal on a LEAD with no job has no drawing to import — the canvas is per job — so the panel
 *     says that rather than showing an empty import that looks broken;
 *   · importing replaces or appends, and it says which, because a silent replace of measured rooms
 *     would throw away work someone did on site.
 */
import {useEffect,useState} from 'react';
import {Link} from 'react-router-dom';
import {useQuery} from '@tanstack/react-query';
import {Button} from '@aiw/ui';
import {AssessmentArea, carpetOrderSummary, type CarpetLayout, type PlanGeometry, type ProposalContent} from '@artemis-group/aiw-contracts';
import {authHeaders, fetchImpl} from '../lib/api';
import {env} from '../lib/env';
import {useT} from '../lib/i18n';
import {ARNCLIFFE_CANDIDATE,candidateProposalLayout} from '../lib/carpet-candidate';

type Room = ProposalContent['assessment']['rooms'][number];
type Area = ProposalContent['assessment']['areas'][number];

type Summary = { revision_no: number | null; revision_label: string | null; revision_state: string | null; rooms: number; area_m2: number; laser_rooms: number; product_name: string | null };
type PlanRow = { floor: string; name: string; traced: boolean; rooms: number; revision: number };
export type CanvasPayload = {
  data: { areas: { id: string; name: string }[]; rooms: Room[] };
  summary: Summary;
  /** The job's measurements, computed at source on the rooms that are in scope. */
  measurements?: {floor_area_m2: number; removal_m2: number; skirting_lm: number; scotia_lm: number; trim_lm: number; doors: number; rooms_in: number; rooms_out: number};
  /** Which anchor answered: the job's measured drawing, the opportunity's traced plan, or neither. */
  source?: 'work_revision' | 'job' | 'opportunity' | 'none';
  binding?: {work_id:string;revision_id:string;content_hash:string;owner_name:string;save_number:number;saved_at:string}|null;
  opportunity_ref?: string | null;
  plans?: PlanRow[];
  /** Pictures the portal can show. Public or portal-served URLs only — never a storage credential. */
  assets?: { id: string; name: string; url: string; floor: string | null }[];
  /** What the canvas says about the whole site — carried in, never asked for twice. */
  whole_site?: {edge_finish: string | null; subfloor: string | null; notes: string | null};
  /** The stairs the drawing records, per floor — the measurement stair work is priced on. */
  stairs?: {floor: string; runs: number; steps: number; kinds: string[]; carpet_lm: number | null}[];
  carpet_layout?: CarpetLayout | null;
  /** Walls, doors and open edges per drawn floor — the plan, not just the room outlines. */
  floors?: {area_id: string; name: string; walls: {a: Pt; b: Pt; thickness_m: number}[]; measured_lines?:{id:string;room:string;label:string;a:Pt;b:Pt;length_m:number}[]; doors: {from: Pt; to: Pt; kind: string}[];
    cutouts?: {room_id:string;kind:string;points:Pt[];m2:number}[];
    /** The scotia and U-channel runs, where they were traced — the plan marks them. */
    trims?: {kind: string; points: Pt[];length_m:number|null;room?:string;location?:string;measurement_confirmed?:boolean}[];
    /** The skirting the app worked out, per floor — what the boards are ordered from. */
    skirting?: {register: string; run_lm: number; skirting_lm: number; scotia_lm: number;
      rooms: {area_id:string;name: string; outline_m: number; doorway_m: number; trim_m: number; skirting_m: number;scotia_m:number}[]};
    open_edges: {a: Pt; b: Pt; kind: string; robe_m2?: number | null; robe_rect?: Pt[] | null}[]}[];
};
type Pt = {x: number; y: number};

/**
 * The drawing, as a SUPPLEMENTARY ASSET on the customer's portal — one button, no decisions.
 *
 * Jacky, 17 September: *"I simply want the canvas drawing as supplementary asset at proposal portal,
 * the numbers (SQM) is what's used, dont over complicate what decision I need to make."* So this panel
 * does two separate things and says which is which:
 *   · **Numbers** (SQM, skirting, floors) come from the rooms — one click, and they feed the price.
 *   · **The drawing itself** is attached to the proposal as a picture the customer sees beside the
 *     offer. It is never an input to a price.
 */
export function CanvasImport({ proposalId, leadId, jobRef, roomCount, attachedCount, onAttach, onImport, onImportCarpetLayout, onBind, boundRevisionId, serverImport = false }: {
  /** A saved site measure is imported by the server (Update from the site measure); this keeps the legacy sources only. */
  serverImport?: boolean;
  onBind: (binding:NonNullable<CanvasPayload['binding']>)=>void;
  boundRevisionId:string|null;
  /** The Proposal: the anchored route resolves the job's drawing or the opportunity's trace. */
  proposalId: string | null;
  leadId: string | null;
  /** A job ref, for a draft that has not been saved yet, so the job's drawing is still reachable. */
  jobRef: string | null;
  roomCount: number;
  /** Attach the drawing the portal should show, and hand back the stored asset. */
  onAttach: (planId: string, name: string) => Promise<void>;
  attachedCount: number;
  /** The drawing's own areas (`id` + floor name); the builder merges them into the assessment. */
  onImport: (areas: { id: string; name: string }[], rooms: Room[], mode: 'replace' | 'append', wholeSite: CanvasPayload['whole_site'] | undefined, stairs: CanvasPayload['stairs'] | undefined, doors: {floor: string; count: number}[] | undefined, plan: PlanGeometry[], measurements: CanvasPayload['measurements'],binding:NonNullable<CanvasPayload['binding']>|null,carpetLayout:CarpetLayout|null) => void;
  /** Bring in only the physical carpet review; preserve all assessed rooms and other site details. */
  onImportCarpetLayout: (layout:CarpetLayout,binding:NonNullable<CanvasPayload['binding']>)=>void;
}) {
  const {t} = useT();
  const [mode, setMode] = useState<'replace' | 'append'>('replace');
  const [busy, setBusy] = useState(false);
  const [selectedRevision,setSelectedRevision]=useState('');
  const revisionList=useQuery({queryKey:['assessment-revisions',proposalId],enabled:!!proposalId,retry:false,queryFn:async()=>{const r=await fetchImpl(`${env.apiBase}/api/proposals/${encodeURIComponent(proposalId!)}/assessment-revisions`,{headers:await authHeaders()});const body=await r.json();if(!r.ok)throw Error(body.error??'Assessment revisions unavailable');return body as {data:Array<{id:string;owner_name:string;save_number:number;saved_at:string;content_hash:string}>;selected_revision_id:string|null;legacy_fallback:boolean};}});
  useEffect(()=>{if(!selectedRevision&&revisionList.data?.data.length)setSelectedRevision(revisionList.data.selected_revision_id??revisionList.data.data[0]!.id);},[revisionList.data,selectedRevision]);
  const path = proposalId ? `/api/proposals/${encodeURIComponent(proposalId)}/canvas-rooms${selectedRevision?`?revision_id=${encodeURIComponent(selectedRevision)}`:''}` : leadId ? `/api/proposal-canvas-rooms?lead_id=${encodeURIComponent(leadId)}` : jobRef ? `/api/proposal-canvas-rooms?job_ref=${encodeURIComponent(jobRef)}` : null;
  const q = useQuery({
    queryKey: ['canvas-rooms', proposalId ?? leadId ?? jobRef,selectedRevision],
    enabled: !!path,
    retry: false,
    queryFn: async (): Promise<CanvasPayload> => {
      const r = await fetchImpl(`${env.apiBase}${path}`, { headers: await authHeaders() });
      const body = await r.json();
      if (!r.ok) throw Error(body.error ?? 'The drawing could not be read');
      return body as CanvasPayload;
    },
  });
  const canvasRef=jobRef??q.data?.opportunity_ref;
  const candidate=canvasRef===ARNCLIFFE_CANDIDATE.ref&&q.data?.binding?.save_number===ARNCLIFFE_CANDIDATE.sourceSave?ARNCLIFFE_CANDIDATE:null;

  // LOUD, NOT A GREY SENTENCE. Jacky, after this shipped: *"did proposal import canvas deployed? I
  // cant see it"* — it was deployed, and the panel was a single muted line on a proposal whose job
  // had no drawing. Every state now says what it IS and what to do next, and the one that can act
  // says so in the header.
  const shell = (children: React.ReactNode, tone: 'ready' | 'empty' = 'empty') => <section className={`rounded-lg border bg-card p-4 ${tone === 'ready' ? 'border-l-4 border-l-primary' : ''}`}>
    <header className="flex flex-wrap items-center justify-between gap-2">
      <h2 className="font-display text-sm font-semibold">{t('canvas.title')}</h2>
      <span className="flex items-center gap-2">
        {tone === 'ready' && <span className="rounded border px-1.5 text-micro text-primary">{t('canvas.ready')}</span>}
        {canvasRef&&q.data?.data.rooms.some(r=>isRollCarpetFinish(r.flooring))&&<Link className="inline-flex min-h-10 items-center rounded-md border px-3 font-medium text-primary" to={`/carpet-run/${encodeURIComponent(canvasRef)}`}>{t('carpetRun.open')}</Link>}
        {jobRef && <span className="text-micro text-muted-foreground">{jobRef}</span>}
      </span>
    </header>
    <div className="mt-2 space-y-2 text-sm">{children}</div>
  </section>;

  if (!path) return shell(<>
    <p>{t('canvas.saveFirst')}</p>
    <p className="text-muted-foreground">{t('canvas.noJobHint')}</p>
  </>);
  if (q.isPending) return shell(<p>{t('canvas.reading')}</p>);
  if (q.isError) return shell(<p role="alert">{(q.error as Error).message}</p>);
  const summary = q.data!.summary;
  const plans = q.data!.plans ?? [];
  if (!summary.rooms && !q.data!.binding) {
    // Nothing traced yet. Say WHICH anchor is missing and, when plans exist, exactly what to trace —
    // he approved "the opportunity carries the plan", so the plan's presence is the useful fact here.
    const untraced = plans.filter(p => !p.traced);
    return shell(<>
      {untraced.length > 0
        ? <p>{t('canvas.plansUntraced', {ref: q.data!.opportunity_ref ?? '', count: String(untraced.length), floors: untraced.map(p => p.floor).join(', ')})}</p>
        : <p>{t('canvas.noPlan')}</p>}
      <p className="text-muted-foreground">{t('canvas.noDrawingHint')}</p>
      {jobRef && <Link className="text-primary underline" to={`/jobs/${jobRef}`}>{t('canvas.openJob')}</Link>}
    </>);
  }

  const { areas, rooms } = q.data!.data;
  const rev = summary.revision_label ?? summary.revision_no ?? '—';
  const source = q.data!.source === 'work_revision'
    ? t('canvas.sharedRevisionSource',{owner:q.data!.binding?.owner_name??'',number:String(q.data!.binding?.save_number??'')})
    : q.data!.source === 'opportunity'
    ? t('canvas.fromOpportunity', {ref: q.data!.opportunity_ref ?? ''})
    : q.data!.source === 'job' ? t('canvas.fromJob') : null;
  return shell(<>
    {canvasRef&&<div className="flex justify-end"><Link className="inline-flex min-h-10 items-center rounded-md border px-3 font-medium text-primary" to={`/work-record/${encodeURIComponent(canvasRef)}`}>{t('canvas.openSiteCanvas')}</Link></div>}
    {!!revisionList.data?.data.length&&<label className="block text-sm">{t('canvas.sharedRevision')}<select className="ml-2 rounded border bg-background px-2 py-1" value={selectedRevision} onChange={e=>setSelectedRevision(e.target.value)}>{revisionList.data.data.map(r=><option key={r.id} value={r.id}>{t('canvas.sharedRevisionOption',{owner:r.owner_name,number:String(r.save_number),date:new Date(r.saved_at).toLocaleString('en-AU',{timeZone:'Australia/Sydney'})})}</option>)}</select></label>}
    {q.data!.binding&&<div className="rounded border p-3 space-y-2"><p>{t('canvas.bindHelp')}</p><Button size="sm" disabled={boundRevisionId===q.data!.binding.revision_id} onClick={()=>onBind(q.data!.binding!)}>{t(boundRevisionId===q.data!.binding.revision_id?'canvas.bound':'canvas.bindOnly')}</Button></div>}
    {q.data!.source!=='work_revision'&&<p className="rounded border border-amber-300 bg-amber-50 p-2 text-xs">{t('canvas.legacyFallback')}</p>}
    {source && <p className="font-medium">{source}</p>}
    <p className="num">
      {t('canvas.summary', {revision: String(rev), rooms: String(summary.rooms), area: new Intl.NumberFormat('en-AU',{maximumFractionDigits:2}).format(summary.area_m2), laser: String(summary.laser_rooms)})}
      {summary.product_name ? ` · ${summary.product_name}` : ''}
    </p>
    <ul className="flex flex-wrap gap-x-4 gap-y-1 text-micro text-muted-foreground">
      {areas.map(a => <li key={a.id}>{a.name} · {rooms.filter(r => r.area_id === a.id).length}</li>)}
      {rooms.some(r => !r.area_id) && <li>{t('canvas.wholeSite')} · {rooms.filter(r => !r.area_id).length}</li>}
    </ul>
    {/* THE DRAWING, REBUILT FROM ITS OWN JSON — Jacky, 18 Sep: *"site assessment I want the canvas
        rebuilt as floor plan, both level shown, they can be in bracket of they own, but same view as
        canvas"*. One panel per drawn floor, the room outlines exactly as the canvas has them (its
        coordinates, so it is the same picture), and **no length × width for an irregular room** — a
        polygon that is not a rectangle shows its drawn area only. */}
    <div className="grid gap-3 xl:grid-cols-2">
      {areas.map((a) => <FloorPlan key={a.id} name={a.name} rooms={rooms.filter((r) => r.area_id === a.id)} geometry={(q.data!.floors ?? []).find((f) => f.area_id === a.id)} />)}
      {rooms.some((r) => !r.area_id) && <FloorPlan name={t('canvas.wholeSite')} rooms={rooms.filter((r) => !r.area_id)} />}
    </div>
    {rooms.some(r=>isRollCarpetFinish(r.flooring))&&<div className="rounded border p-3 text-sm">
      <strong>{t('canvas.carpetLayout')}</strong>
      {q.data!.carpet_layout?(()=>{const layout=q.data!.carpet_layout!;const order=carpetOrderSummary(layout);return <div className="mt-1 space-y-2"><p>{t('canvas.carpetOrder',{cuts:String(layout.cuts.length),metres:order.cutLengthM.toFixed(2),whole:String(order.wholeLm),width:layout.roll_width_m.toFixed(2),area:order.chargeableM2.toFixed(2)})}</p>{q.data!.binding&&<Button size="sm" variant="outline" onClick={()=>onImportCarpetLayout(layout,q.data!.binding!)}>{t('canvas.importCarpetOnly')}</Button>}<p className="text-xs text-muted-foreground">{t('canvas.importCarpetOnlyHelp')}</p></div>})()
       :<div className="mt-1"><p className="text-amber-800">{t('canvas.carpetReviewNeeded')}</p>{canvasRef&&<Link className="mt-2 inline-flex rounded-md border px-3 py-2 font-medium text-primary" to={`/carpet-run/${encodeURIComponent(canvasRef)}`}>{t('carpetRun.open')}</Link>}</div>}
      {candidate&&q.data!.binding&&<div className="mt-3 rounded border border-amber-300 bg-amber-50 p-3"><p className="font-semibold">{t('canvas.carpetCandidateStatus')}</p><p className="mt-1">{t('canvas.carpetOrder',{cuts:'4',metres:'28.53',whole:'29',width:'3.66',area:'106.14'})}</p><p className="mt-1 text-xs">{t('canvas.carpetCandidateHelp')}</p><Button className="mt-2" size="sm" onClick={()=>onImportCarpetLayout(candidateProposalLayout(candidate,q.data!.binding!.revision_id),q.data!.binding!)}>{t('canvas.carpetCandidateImport')}</Button></div>}
    </div>}
    {/* Carpet tiles are priced per m² like any tiled floor: say so, so nobody goes looking for a roll cut plan (18 Oct 2026). */}
    {rooms.some(r=>isCarpetTileFinish(r.flooring)&&!r.out_of_scope&&!r.is_void)&&<p className="rounded border p-3 text-sm">{t('canvas.carpetTileNote',{area:rooms.filter(r=>isCarpetTileFinish(r.flooring)&&!r.out_of_scope&&!r.is_void).reduce((n,r)=>n+(r.area_m2??0),0).toFixed(2)})}</p>}
    {rooms.some(r => r.source === 'floor_plan') && <p className="text-micro text-muted-foreground">{t('canvas.planOnly', {count: String(rooms.filter(r => r.source === 'floor_plan').length)})}</p>}
    {serverImport&&q.data!.source==='work_revision'&&<p className="text-sm text-muted-foreground">Rooms, products and trims from this saved measure come in through <strong>Update from the site measure</strong> above.</p>}
    {rooms.length>0&&!(serverImport&&q.data!.source==='work_revision')&&<div className="flex flex-wrap items-center gap-2">
      {roomCount > 0 && <>
        <Button size="sm" variant={mode === 'replace' ? 'default' : 'outline'} aria-pressed={mode === 'replace'} onClick={() => setMode('replace')}>{t('canvas.modeReplace')}</Button>
        <Button size="sm" variant={mode === 'append' ? 'default' : 'outline'} aria-pressed={mode === 'append'} onClick={() => setMode('append')}>{t('canvas.modeAppend')}</Button>
      </>}
      <Button size="sm" disabled={!rooms.length} onClick={() => onImport(areas, rooms, roomCount > 0 ? mode : 'replace', q.data!.whole_site, q.data!.stairs,
        (q.data!.floors ?? []).map(f => ({floor: f.name, count: f.doors.length})),
        buildPlan(q.data!.floors ?? [], rooms), q.data!.measurements,q.data!.binding??null,q.data!.carpet_layout??null)}>
        {roomCount > 0 && mode === 'replace'
          ? t('canvas.importReplace', {count: String(roomCount)})
          : t('canvas.import', {count: String(rooms.length)})}
      </Button>
    </div>}
    {rooms.length>0 && roomCount > 0 && mode === 'replace' && <p className="text-micro text-muted-foreground">{t('canvas.replaceWarning', {count: String(roomCount)})}</p>}
    {(q.data!.assets ?? []).length > 0 && <div className="border-t pt-2">
      <p className="text-micro text-muted-foreground">{t('canvas.assetsNote')}</p>
      <ul className="mt-1 space-y-1">{q.data!.assets!.map(asset => <li key={asset.id} className="flex flex-wrap items-center justify-between gap-2 text-sm">
        <span>{asset.name}{asset.floor ? <span className="text-muted-foreground"> · {asset.floor}</span> : null}</span>
        <Button size="sm" variant="outline" disabled={busy} onClick={() => { setBusy(true); void onAttach(asset.id, asset.name).finally(() => setBusy(false)); }}>{t('canvas.attach')}</Button>
      </li>)}</ul>
      {attachedCount > 0 && <p className="text-micro text-muted-foreground">{t('canvas.attachedCount', {count: String(attachedCount)})}</p>}
    </div>}
  </>, rooms.length?'ready':'empty');
}

/**
 * ONE FLOOR, AS THE CANVAS DRAWS IT.
 *
 * The room outlines come straight from the drawing's own coordinates, so this is the same picture he
 * made rather than a re-interpretation of it. Two deliberate refusals:
 *
 *   · **no length × width for an irregular room** (*"i dont need length and width shown for irregular
 *     shape"*) — a polygon that is not a rectangle shows its drawn area, full stop. `asRectangle` in
 *     the API is what decides, and an L-shaped ground floor keeps its 22 m² and no dimensions.
 *   · **no axes, no scale bar, no grid** — it is his drawing, not a surveyor's plan. The measure that
 *     matters (m², and the flooring he set) is written in the room.
 */
function FloorPlan({name, rooms, geometry}: {name: string; rooms: Room[]; geometry?: {walls: {a: Pt; b: Pt; thickness_m: number}[]; measured_lines?:{id:string;room:string;label:string;a:Pt;b:Pt;length_m:number}[]; doors: {from: Pt; to: Pt; kind: string}[]; cutouts?:{room_id:string;kind:string;points:Pt[];m2:number}[]; open_edges: {a: Pt; b: Pt; kind: string; robe_m2?: number | null; robe_rect?: Pt[] | null}[]}}) {
  const {t} = useT();
  const drawn = rooms.filter((r) => (r.shape ?? []).length >= 3);
  if (!drawn.length) return null;
  const points = drawn.flatMap((r) => r.shape ?? []);
  const xs = points.map((p) => p.x);
  const ys = points.map((p) => p.y);
  const minX = Math.min(...xs), maxX = Math.max(...xs), minY = Math.min(...ys), maxY = Math.max(...ys);
  const w = Math.max(maxX - minX, 0.5), h = Math.max(maxY - minY, 0.5);
  const span = Math.max(w, h);
  const pad = span * 0.05;
  const wall = Math.max(span * 0.008, 0.05);
  const area = drawn.reduce((sum, r) => sum + (r.area_m2 ?? 0), 0);
  // WARDROBE FLOOR SPACE COUNTS — and it is already inside each room's area, because the flooring runs
  // under it. This total is only for saying how much of the floor that is.
  const robes = (geometry?.open_edges ?? []).reduce((sum, e) => sum + (e.robe_m2 ?? 0), 0);

  // A LABEL THAT DOES NOT OVERGROW ITS ROOM. Jacky, 18 Sep: *"room name that dont overgrow"*. The font
  // is sized to the room the label sits in, capped so a big room does not shout, and a room too small
  // to hold its name at a legible size gets none — the room is still drawn, and the table under the
  // plan still lists it. A name spilling over three neighbours is worse than no name.
  const label = (r: Room) => {
    const shape = r.shape ?? [];
    const roomSpan = Math.max(
      Math.max(...shape.map((p) => p.x)) - Math.min(...shape.map((p) => p.x)),
      Math.max(...shape.map((p) => p.y)) - Math.min(...shape.map((p) => p.y)),
    );
    const chars = Math.max(r.name.length, 4);
    const byWidth = (roomSpan * 0.82) / (chars * 0.56);
    const size = Math.min(span * 0.028, roomSpan * 0.22, byWidth);
    return size >= span * 0.011 ? size : null;
  };

  return <section className="rounded border bg-white p-3">
    <p className="mb-2 flex items-baseline justify-between gap-2">
      <span className="text-sm font-medium">{name}</span>
      {/* SQUARE METRES, NOT DOLLARS. This read `money(area)` and printed "$66.57 m²" — a price on a
          measurement, on the tab where prices are decided. */}
      {/* The rooms' areas already include their wardrobes ("flooring goes under"), so the robe share is
          shown as part of the total, not added to it. */}
      <span className="num text-micro text-muted-foreground">{t('canvas.floorPlanSummary', {rooms: String(drawn.length), area: `${Math.round(area * 100) / 100} m²`})}{robes > 0 ? ` · ${t('canvas.robeIncluded', {area: `${Math.round(robes * 100) / 100} m²`})}` : ''}</span>
    </p>
    <svg viewBox={`${minX - pad} ${minY - pad} ${w + pad * 2} ${h + pad * 2}`} className="h-auto max-h-[380px] w-full" role="img"
      aria-label={t('canvas.floorPlanAlt', {name, rooms: String(drawn.length)})}>
      {/* the rooms, as drawn */}
      {drawn.map((r) => <path key={r.id} d={roomSvgPath(r.shape??[],r.shape_holes??[])} fillRule="evenodd" fill="#f2f6f5" stroke="none" />)}
      {(geometry?.cutouts ?? []).map((c,i) => <polygon key={`cutout-${i}`} points={c.points.map(p=>`${p.x},${p.y}`).join(' ')} fill="#fff" stroke="#b98b4a" strokeWidth={wall*0.7} strokeDasharray={`${wall*1.5} ${wall}`} aria-label={`${c.kind}: ${c.m2.toFixed(2)} m²`} />)}
      {/* open-plan boundaries and robe fronts: no wall there, so dashed */}
      {(geometry?.open_edges ?? []).map((e, i) => <g key={`oe-${i}`}>
        {/* A WARDROBE IS AN AREA, SO IT IS DRAWN AS ONE.
            Jacky, 19 Sep: *"the wardrobe in bedroom still not showing up, the actual area has nothing
            defined, its connected to the room and gets room attribute"*. The canvas gives a robe a front
            line and an area; the reader now extrudes the rectangle the area implies, and the plan fills
            it — the wardrobe appears as the space it is, inside the room whose flooring runs under it. */}
        {e.robe_rect
          ? <>
              <polygon points={e.robe_rect.map((p) => `${p.x},${p.y}`).join(' ')} fill="#f0e0c8" stroke="#b98b4a" strokeWidth={wall * 0.8} />
              <text x={e.robe_rect.reduce((n, p) => n + p.x, 0) / e.robe_rect.length} y={e.robe_rect.reduce((n, p) => n + p.y, 0) / e.robe_rect.length}
                fontSize={Math.min(span * 0.016, wall * 3)} textAnchor="middle" fill="#8a6d3b">
                {t('canvas.robeLabel', {area: `${Math.round((e.robe_m2 ?? 0) * 100) / 100} m²`})}
              </text>
            </>
          : <line x1={e.a.x} y1={e.a.y} x2={e.b.x} y2={e.b.y} stroke={e.robe_m2 ? '#b98b4a' : '#9db3b8'} strokeWidth={wall * (e.robe_m2 ? 1.1 : 0.5)} strokeDasharray={e.robe_m2 ? undefined : `${span * 0.02} ${span * 0.015}`} />}
      </g>)}
      {/* THE WALLS, WITH THEIR DOORS CUT OUT — this is what makes it a plan rather than a blob */}
      {(geometry?.walls ?? []).map((wallLine, i) => {
        const dx = wallLine.b.x - wallLine.a.x, dy = wallLine.b.y - wallLine.a.y;
        const len = Math.hypot(dx, dy);
        if (len <= 0) return null;
        const ux = dx / len, uy = dy / len;
        type Cut = {from: number; to: number};
        const cuts: Cut[] = [];
        for (const d of geometry?.doors ?? []) {
          const t0 = ((d.from.x - wallLine.a.x) * ux + (d.from.y - wallLine.a.y) * uy) / len;
          const t1 = ((d.to.x - wallLine.a.x) * ux + (d.to.y - wallLine.a.y) * uy) / len;
          const mid = (t0 + t1) / 2;
          const onThisWall = Math.abs((d.from.x - wallLine.a.x) * -uy + (d.from.y - wallLine.a.y) * ux) < wallLine.thickness_m;
          if (!onThisWall) continue;
          const lo = Math.max(0, Math.min(t0, t1)), hi = Math.min(1, Math.max(t0, t1));
          if (hi - lo <= 0.001) continue;
          cuts.push({from: lo, to: hi});
          void mid;
        }
        cuts.sort((a, b) => a.from - b.from);
        const segments: Cut[] = [];
        let cursor = 0;
        for (const cut of cuts) {
          if (cut.from > cursor) segments.push({from: cursor, to: cut.from});
          cursor = Math.max(cursor, cut.to);
        }
        if (cursor < 1) segments.push({from: cursor, to: 1});
        return <g key={`w-${i}`}>
          {segments.map((seg, si) => <line key={si}
            x1={wallLine.a.x + ux * len * seg.from} y1={wallLine.a.y + uy * len * seg.from}
            x2={wallLine.a.x + ux * len * seg.to} y2={wallLine.a.y + uy * len * seg.to}
            stroke="#175e65" strokeWidth={wall} strokeLinecap="square" />)}
        </g>;
      })}
      {/* the doors: a leaf across the gap, which is how a plan shows one */}
      {(geometry?.doors ?? []).map((d, i) => <line key={`d-${i}`}
        x1={d.from.x} y1={d.from.y} x2={d.to.x} y2={d.to.y}
        stroke="#8a6d3b" strokeWidth={wall * 0.7} strokeLinecap="round" opacity={0.85} />)}
      {(geometry?.measured_lines??[]).filter(m=>/^(width|length)$/i.test(m.label)).map(m=>{
        const x=(m.a.x+m.b.x)/2,y=(m.a.y+m.b.y)/2;
        const vertical=Math.abs(m.b.y-m.a.y)>Math.abs(m.b.x-m.a.x);
        return <text key={`measure-${m.id}`} x={x} y={y-wall*2} transform={vertical?`rotate(-90 ${x} ${y})`:undefined}
          textAnchor="middle" fontSize={span*0.015} fontWeight="700" fill="#075d74" stroke="#fff" strokeWidth={span*0.003} paintOrder="stroke">{m.length_m.toFixed(2)} m</text>;
      })}
      {/* names, only where they fit */}
      {drawn.map((r) => {
        const size = label(r);
        if (!size) return null;
        const shape = r.shape ?? [];
        const cx = shape.reduce((sum, p) => sum + p.x, 0) / shape.length;
        const cy = shape.reduce((sum, p) => sum + p.y, 0) / shape.length;
        return <g key={`l-${r.id}`}>
          <text x={cx} y={cy} fontSize={size} textAnchor="middle" fill="#14343a" style={{fontWeight: 600}}>{r.name}</text>
        </g>;
      })}
    </svg>
    <ul className="mt-2 grid gap-x-3 gap-y-1 text-xs sm:grid-cols-2">
      {drawn.map(r=><li key={r.id} className="flex justify-between gap-2 border-b py-1"><span>{r.name}{r.cutout_m2>0&&<small className="block text-amber-800">{t('canvas.fixtureExcluded',{area:r.cutout_m2.toFixed(2)})}</small>}</span><span className="num whitespace-nowrap">{r.area_m2===null?'—':`${r.area_m2.toFixed(2)} m²`}{r.flooring?` · ${r.flooring}`:''}</span></li>)}
    </ul>
    {!!geometry?.measured_lines?.length&&<details className="mt-2 text-xs"><summary>{t('canvas.recordedReadings',{count:String(geometry.measured_lines.length)})}</summary><ul className="mt-1 grid gap-1 sm:grid-cols-2">{geometry.measured_lines.map(m=><li key={m.id}>{m.room} · {m.label}: <strong>{m.length_m.toFixed(2)} m</strong></li>)}</ul></details>}
  </section>;
}

/** The plan and the area merge moved to the shared contracts (proposal-from-measure.ts), so the server builds the same Proposal. */
export {buildPlan,mergeAreas};
